// dataMapping.js - Updated with route-specific trip mapping
import {
    parseTimeToMinutes,
    filterVehiclesWithData,
    getVehicleNameFromDeviceId,
} from "./dataUtils";

export const mapReportDataToChartData = (
    reportData,
    chartConfig,
    vehiclesData = []
) => {
    if (!reportData?.data || !Array.isArray(reportData.data)) {
        return [];
    }

    const data = reportData.data;
    const { title, routeName, routeKey } = chartConfig; // Add routeName to handle route-specific charts
    let chartData = [];

    switch (title) {
        case "Overspeed Incidents":
            chartData = Object.entries(
                data.reduce((acc, row) => {
                    const vehicle =
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle";
                    acc[vehicle] = (acc[vehicle] || 0) + 1;
                    return acc;
                }, {})
            ).map(([vehicle, count]) => ({ vehicle, count }));
            return filterVehiclesWithData(chartData, "count");

        case "Maximum Speed":
            const maxSpeedLimit = reportData.maxSpeed || 0;
            // console.log("Max Speed Limit:", reportData);
            chartData = Object.entries(
                data.reduce((acc, row) => {
                    const vehicle =
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle";
                    const speed = parseFloat(row.speed) || 0;
                    if (speed > 0) {
                        acc[vehicle] = Math.max(acc[vehicle] || 0, speed);
                    }
                    return acc;
                }, {})
            ).map(([vehicle, speed]) => ({ vehicle, speed }));
            return filterVehiclesWithData(chartData, "speed");

        case "Total Runtime ":
            chartData = data
                .filter((row) => row.totalMovingHours > 0)
                .map((row) => ({
                    vehicle:
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle",
                    hours: parseFloat(row.totalMovingHours) || 0,
                }));
            return filterVehiclesWithData(chartData, "hours");

        case "Route Deviations":
            chartData = data
                .filter((row) => row.outOfRouteCount > 0)
                .map((row) => ({
                    vehicle:
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle",
                    deviations: parseInt(row.outOfRouteCount) || 0,
                }));
            return filterVehiclesWithData(chartData, "deviations");

        case "Overall Average Speed":
            chartData = data
                .filter((row) => row.averageSpeed > 0)
                .map((row) => ({
                    vehicle:
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle",
                    speed: parseFloat(row.averageSpeed) || 0,
                }));
            return filterVehiclesWithData(chartData, "speed");

        case "Distance Covered":
            chartData = data
                .filter((row) => parseFloat(row.noOfKmsCovered) > 0)
                .map((row) => ({
                    vehicle:
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle",
                    distance: parseFloat(row.noOfKmsCovered) || 0,
                }));
            return filterVehiclesWithData(chartData, "distance");

        case "Trips ":
            const tripStatusCounts = data.reduce((acc, row) => {
                const status = String(row.status || "");

                if (status === "Completed") {
                    acc["On Time"] = (acc["On Time"] || 0) + 1;
                } else if (status.includes("Not Completed") || status.includes("over")) {
                    acc["Delayed"] = (acc["Delayed"] || 0) + 1;
                }

                return acc;
            }, {});

            const tripStatusData = Object.entries(tripStatusCounts)
                .filter(([status, count]) => count > 0)
                .map(([status, count]) => ({ status, count }));

            // If no trips, show "No Trips"
            if (tripStatusData.length === 0) {
                tripStatusData.push({ status: "No Trips", count: 1 });
            }

            return tripStatusData;

        case "Vehicle Wise Trips":
            const vehicleTripCounts = data.reduce((acc, row) => {
                const vehicle = row.vehicleName || "Unknown Vehicle";
                const status = String(row.status || "");

                if (!acc[vehicle]) {
                    acc[vehicle] = { onTime: 0, delayed: 0 };
                }

                if (status === "Completed") {
                    acc[vehicle].onTime += 1;
                } else if (status.includes("Not Completed") || status.includes("over")) {
                    acc[vehicle].delayed += 1;
                }

                return acc;
            }, {});

            chartData = Object.entries(vehicleTripCounts)
                .filter(([vehicle, counts]) => counts.onTime > 0 || counts.delayed > 0)
                .map(([vehicle, counts]) => ({
                    vehicle,
                    onTime: counts.onTime,
                    delayed: counts.delayed,
                }));

            return chartData;


        case "Route Specific Trips by Vehicle":
            // Filter data for the specific route and group by vehicle
            const routeSpecificData = data.filter(row => getTripRouteKey(row) === (routeKey || routeName));

            const routeVehicleCounts = routeSpecificData.reduce((acc, row) => {
                const vehicle = row.vehicleName || "Unknown Vehicle";
                const status = String(row.status || "");

                if (!acc[vehicle]) {
                    acc[vehicle] = { onTime: 0, delayed: 0, total: 0 };
                }

                if (status === "Completed") {
                    acc[vehicle].onTime += 1;
                } else if (status.includes("Not Completed") || status.includes("over")) {
                    acc[vehicle].delayed += 1;
                }
                acc[vehicle].total += 1;

                return acc;
            }, {});

            chartData = Object.entries(routeVehicleCounts)
                .filter(([vehicle, counts]) => counts.total > 0)
                .map(([vehicle, counts]) => ({
                    vehicle,
                    onTime: counts.onTime,
                    delayed: counts.delayed,
                    total: counts.total
                }));

            return chartData;

        case "Vehicle Halts":
            return mapHaltData(reportData, vehiclesData, "total");

        case "Vehicle Halts Duration":
            return mapHaltDurationData(reportData, vehiclesData, "total");

        case "Dead Halts":
            return mapHaltData(reportData, vehiclesData, "dead");

        case "Inactive Halts":
            return mapHaltData(reportData, vehiclesData, "inactive");

        case "Dead Halts Duration":
            return mapHaltDurationData(reportData, vehiclesData, "dead");

        case "Inactive Halts Duration":
            return mapHaltDurationData(reportData, vehiclesData, "inactive");

        case "Trips by Time of Day":
            // Group by hour only
            const hourData = {};

            // Process trip data to count both start and end times by hour
            data.forEach(row => {
                // Process start times - extract hour only
                if (row.startTime) {
                    const hour = parseInt(row.startTime.split(':')[0]); // Gets hour as integer

                    if (!hourData[hour]) {
                        hourData[hour] = { startCount: 0, endCount: 0 };
                    }
                    hourData[hour].startCount += 1;
                }

                // Process end times - extract hour only
                if (row.endTime) {
                    const hour = parseInt(row.endTime.split(':')[0]); // Gets hour as integer

                    if (!hourData[hour]) {
                        hourData[hour] = { startCount: 0, endCount: 0 };
                    }
                    hourData[hour].endCount += 1;
                }
            });

            // Convert to array and sort by hour - ENSURE INTEGER VALUES
            return Object.entries(hourData)
                .map(([hour, counts]) => ({
                    time: parseInt(hour), // Use integer hour for X-axis (8, 9, 10, etc.)
                    startCount: Math.floor(counts.startCount),
                    endCount: Math.floor(counts.endCount)
                }))
                .sort((a, b) => a.time - b.time); // Sort numerically by hour

        case "Stops Visited":
            chartData = data
                .filter((row) => row.noOfStopsCovered > 0)
                .map((row) => ({
                    vehicle:
                        row.vehicleName ||
                        row.deviceName ||
                        `Vehicle-${row.deviceId}` ||
                        "Unknown Vehicle",
                    stops: parseInt(row.noOfStopsCovered) || 0,
                }));
            return filterVehiclesWithData(chartData, "stops");

        case "Route Wise Trips":
            // Group trips by route and count on time vs delayed
            const routeTripCounts = data.reduce((acc, row) => {
                const route = row.routeName || "Unknown Route";
                const status = String(row.status || "");

                if (!acc[route]) {
                    acc[route] = { onTime: 0, delayed: 0 };
                }

                if (status === "Completed") {
                    acc[route].onTime += 1;
                } else if (status.includes("Not Completed") || status.includes("over")) {
                    acc[route].delayed += 1;
                }

                return acc;
            }, {});

            // Convert to chart data format with both onTime and delayed counts
            chartData = Object.entries(routeTripCounts)
                .filter(([route, counts]) => counts.onTime > 0 || counts.delayed > 0)
                .map(([route, counts]) => ({
                    route,
                    onTime: counts.onTime,
                    delayed: counts.delayed,
                    total: counts.onTime + counts.delayed // Add total for reference
                }));

            return chartData;

        case "Trip Starts":
            // Group trip starts by time periods (4 time slots)
            const startTimePeriods = {
                "12 AM - 6 AM": 0,
                "6 AM - 12 PM": 0,
                "12 PM - 6 PM": 0,
                "6 PM - 12 AM": 0
            };

            data.forEach(row => {
                if (row.startTime) {
                    const hour = parseInt(row.startTime.split(':')[0]);

                    if (hour >= 0 && hour < 6) {
                        startTimePeriods["12 AM - 6 AM"]++;
                    } else if (hour >= 6 && hour < 12) {
                        startTimePeriods["6 AM - 12 PM"]++;
                    } else if (hour >= 12 && hour < 18) {
                        startTimePeriods["12 PM - 6 PM"]++;
                    } else {
                        startTimePeriods["6 PM - 12 AM"]++;
                    }
                }
            });

            return Object.entries(startTimePeriods)
                .filter(([period, count]) => count > 0)
                .map(([period, count]) => ({
                    status: period,
                    count: count
                }));

        case "Trip Ends":
            // Group trip ends by time periods (4 time slots)
            const endTimePeriods = {
                "12 AM - 6 AM": 0,
                "6 AM - 12 PM": 0,
                "12 PM - 6 PM": 0,
                "6 PM - 12 AM": 0
            };

            data.forEach(row => {
                if (row.endTime) {
                    const hour = parseInt(row.endTime.split(':')[0]);

                    if (hour >= 0 && hour < 6) {
                        endTimePeriods["12 AM - 6 AM"]++;
                    } else if (hour >= 6 && hour < 12) {
                        endTimePeriods["6 AM - 12 PM"]++;
                    } else if (hour >= 12 && hour < 18) {
                        endTimePeriods["12 PM - 6 PM"]++;
                    } else {
                        endTimePeriods["6 PM - 12 AM"]++;
                    }
                }
            });

            return Object.entries(endTimePeriods)
                .filter(([period, count]) => count > 0)
                .map(([period, count]) => ({
                    status: period,
                    count: count
                }));

        case "Trips Start and End":
            // Group both starts and ends by time periods for attached (grouped) bar chart
            const timePeriods = [
                { period: "12 AM - 6 AM", startHour: 0, endHour: 6 },
                { period: "6 AM - 12 PM", startHour: 6, endHour: 12 },
                { period: "12 PM - 6 PM", startHour: 12, endHour: 18 },
                { period: "6 PM - 12 AM", startHour: 18, endHour: 24 }
            ];

            const periodCounts = timePeriods.map(({ period, startHour, endHour }) => {
                let tripStarts = 0;
                let tripEnds = 0;

                data.forEach(row => {
                    if (row.startTime) {
                        const hour = parseInt(row.startTime.split(':')[0]);
                        if (hour >= startHour && hour < endHour) {
                            tripStarts++;
                        }
                    }

                    if (row.endTime) {
                        const hour = parseInt(row.endTime.split(':')[0]);
                        if (hour >= startHour && hour < endHour) {
                            tripEnds++;
                        }
                    }
                });

                return {
                    period: period,
                    tripStarts: tripStarts,
                    tripEnds: tripEnds
                };
            });

            return periodCounts.filter(item => item.tripStarts > 0 || item.tripEnds > 0);
            
        default:
            return [];
    }
};

// Helper function to get unique routes from trip data
export const getUniqueRoutesFromTripData = (reportData) => {
    if (!reportData?.data || !Array.isArray(reportData.data)) {
        return [];
    }

    const routeMap = new Map();
    reportData.data.forEach((trip) => {
        const routeName = trip.routeName || trip.route || trip.routeId || "Unknown Route";
        const routeKey = getTripRouteKey(trip);
        if (!routeMap.has(routeKey)) {
            routeMap.set(routeKey, { routeKey, routeName });
        }
    });

    return Array.from(routeMap.values()).sort((a, b) =>
        a.routeName.localeCompare(b.routeName)
    );
};

const getTripRouteKey = (trip) =>
    String(
        trip?.routeId ||
        trip?.route_id ||
        trip?.routeCode ||
        trip?.routeName ||
        trip?.route ||
        "Unknown Route"
    ).trim() || "Unknown Route";

// Rest of the helper functions remain the same...
const mapHaltData = (reportData, vehiclesData, type) => {
    const data = reportData.data;

    if (reportData.devices && Array.isArray(reportData.devices)) {
        return reportData.devices
            .map((device) => {
                const vehicle = getVehicleNameFromDeviceId(device.deviceId, vehiclesData);

                // FIXED: Ensure proper field mapping for inactive counts
                const inactiveCount = device.totalInactivePeriods || device.inactivePeriods || 0;
                const deadCount = device.totalDeadPeriods || device.deadPeriods || 0;

                if (type === "total") {
                    return {
                        vehicle,
                        inactive: parseInt(inactiveCount) || 0,
                        dead: parseInt(deadCount) || 0,
                    };
                } else if (type === "dead") {
                    return {
                        vehicle,
                        dead: parseInt(deadCount) || 0,
                    };
                } else if (type === "inactive") {
                    return {
                        vehicle,
                        inactive: parseInt(inactiveCount) || 0,
                    };
                }
            })
            .filter((item) => {
                if (type === "total") return item.inactive > 0 || item.dead > 0;
                if (type === "dead") return item.dead > 0;
                if (type === "inactive") return item.inactive > 0;
                return false;
            });
    } else {
        const groupedData = data.reduce((acc, row) => {
            const vehicle =
                row.vehicleName ||
                getVehicleNameFromDeviceId(row.vehicleNumber, vehiclesData);
            if (!acc[vehicle]) {
                acc[vehicle] = { inactive: 0, dead: 0 };
            }

            // FIXED: Better type matching for inactive halts
            const haltType = row.type || row.haltType || '';
            if (haltType.toLowerCase().includes('inactive') || haltType === 'Inactive') {
                acc[vehicle].inactive += 1;
            } else if (haltType.toLowerCase().includes('dead') || haltType === 'Dead') {
                acc[vehicle].dead += 1;
            }
            return acc;
        }, {});

        return Object.entries(groupedData)
            .map(([vehicle, counts]) => {
                if (type === "total") {
                    return {
                        vehicle,
                        inactive: parseInt(counts.inactive) || 0,
                        dead: parseInt(counts.dead) || 0
                    };
                } else if (type === "dead") {
                    return {
                        vehicle,
                        dead: parseInt(counts.dead) || 0
                    };
                } else if (type === "inactive") {
                    return {
                        vehicle,
                        inactive: parseInt(counts.inactive) || 0
                    };
                }
            })
            .filter((item) => {
                if (type === "total") return item.inactive > 0 || item.dead > 0;
                if (type === "dead") return item.dead > 0;
                if (type === "inactive") return item.inactive > 0;
                return false;
            });
    }
};

const mapHaltDurationData = (reportData, vehiclesData, type) => {
    const data = reportData.data;

    if (reportData.devices && Array.isArray(reportData.devices)) {
        return reportData.devices
            .map((device) => {
                const vehicle = getVehicleNameFromDeviceId(device.deviceId, vehiclesData);

                // FIXED: Better field mapping for inactive duration
                const inactiveTimeString = device.totalInactiveTime?.string ||
                    device.inactiveTime?.string ||
                    device.totalInactiveTime ||
                    "0h 0m 0s";
                const deadTimeString = device.totalDeadTime?.string ||
                    device.deadTime?.string ||
                    device.totalDeadTime ||
                    "0h 0m 0s";

                const inactiveMinutes = parseTimeToMinutes(inactiveTimeString);
                const deadMinutes = parseTimeToMinutes(deadTimeString);

                if (type === "total") {
                    return {
                        vehicle,
                        inactive: Math.round((inactiveMinutes / 60) * 100) / 100,
                        dead: Math.round((deadMinutes / 60) * 100) / 100,
                    };
                } else if (type === "dead") {
                    return {
                        vehicle,
                        dead: Math.round((deadMinutes / 60) * 100) / 100,
                    };
                } else if (type === "inactive") {
                    return {
                        vehicle,
                        inactive: Math.round((inactiveMinutes / 60) * 100) / 100,
                    };
                }
            })
            .filter((item) => {
                if (type === "total") return item.inactive > 0 || item.dead > 0;
                if (type === "dead") return item.dead > 0;
                if (type === "inactive") return item.inactive > 0;
                return false;
            });
    } else {
        const groupedData = data.reduce((acc, row) => {
            const vehicle =
                row.vehicleName ||
                getVehicleNameFromDeviceId(row.vehicleNumber, vehiclesData);
            if (!acc[vehicle]) {
                acc[vehicle] = { inactiveMinutes: 0, deadMinutes: 0 };
            }

            const duration = parseTimeToMinutes(row.duration || "0h 0m 0s");

            // FIXED: Better type matching for inactive duration
            const haltType = row.type || row.haltType || '';
            if (haltType.toLowerCase().includes('inactive') || haltType === 'Inactive') {
                acc[vehicle].inactiveMinutes += duration;
            } else if (haltType.toLowerCase().includes('dead') || haltType === 'Dead') {
                acc[vehicle].deadMinutes += duration;
            }
            return acc;
        }, {});

        return Object.entries(groupedData)
            .map(([vehicle, durations]) => {
                if (type === "total") {
                    return {
                        vehicle,
                        inactive: Math.round((durations.inactiveMinutes / 60) * 100) / 100,
                        dead: Math.round((durations.deadMinutes / 60) * 100) / 100,
                    };
                } else if (type === "dead") {
                    return {
                        vehicle,
                        dead: Math.round((durations.deadMinutes / 60) * 100) / 100,
                    };
                } else if (type === "inactive") {
                    return {
                        vehicle,
                        inactive: Math.round((durations.inactiveMinutes / 60) * 100) / 100,
                    };
                }
            })
            .filter((item) => {
                if (type === "total") return item.inactive > 0 || item.dead > 0;
                if (type === "dead") return item.dead > 0;
                if (type === "inactive") return item.inactive > 0;
                return false;
            });
    }
};

export const mapTripDataForMultiVehicleChart = (reportData) => {
    if (!reportData?.data || !Array.isArray(reportData.data)) {
        return [];
    }

    const data = reportData.data;

    // Create time slots based on date range (similar to "Trips by Time of Day")
    const timeSlots = {};
    const dateRange = reportData.dateRange;

    // Determine time slot interval based on date range
    let slotInterval = 1; // 1 hour slots by default
    if (dateRange) {
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        // Adjust interval based on date range
        if (daysDiff <= 1) {
            slotInterval = 1; // 1-hour slots for single day
        } else if (daysDiff <= 7) {
            slotInterval = 2; // 2-hour slots for week
        } else {
            slotInterval = 4; // 4-hour slots for longer periods
        }
    }

    // Process trip data to count trips by time slots for each vehicle
    data.forEach(row => {
        if (row.startTime && row.vehicleName) {
            const [hours, minutes] = row.startTime.split(':').map(Number);
            const slotStart = Math.floor(hours / slotInterval) * slotInterval;
            const slotEnd = slotStart + slotInterval;
            const timeSlot = `${slotStart.toString().padStart(2, '0')}:00-${slotEnd.toString().padStart(2, '0')}:00`;

            if (!timeSlots[timeSlot]) {
                timeSlots[timeSlot] = {};
            }

            if (!timeSlots[timeSlot][row.vehicleName]) {
                timeSlots[timeSlot][row.vehicleName] = 0;
            }

            timeSlots[timeSlot][row.vehicleName]++;
        }
    });

    // Get all unique vehicles
    const allVehicles = new Set();
    Object.values(timeSlots).forEach(slot => {
        Object.keys(slot).forEach(vehicle => allVehicles.add(vehicle));
    });

    // Convert to array format and ensure all vehicles have entries for each time slot
    const chartData = Object.entries(timeSlots).map(([time, vehicleData]) => {
        const timeData = { time };

        // Add all vehicles to this time slot (0 if no trips)
        Array.from(allVehicles).forEach(vehicle => {
            timeData[vehicle] = vehicleData[vehicle] || 0;
        });

        return timeData;
    });

    // Sort by time
    return chartData.sort((a, b) => {
        const aHour = parseInt(a.time.split(':')[0]);
        const bHour = parseInt(b.time.split(':')[0]);
        return aHour - bHour;
    });
};

// Function to map trip data for Gantt-style timeline
export const mapTripDataForGanttChart = (reportData) => {
    if (!reportData?.data || !Array.isArray(reportData.data)) {
        return [];
    }

    const data = reportData.data;

    // Group trips by vehicle number
    const groupedByVehicle = data.reduce((acc, trip) => {
        const vehicleNumber = trip.vehicleNumber;
        if (!acc[vehicleNumber]) {
            acc[vehicleNumber] = {
                vehicleName: trip.vehicleName || "Unknown Vehicle",
                trips: []
            };
        }

        const startTime = trip.startTime || "00:00:00";
        const endTime = trip.endTime || "00:00:00";

        // Convert time to minutes from start of day (00:00)
        const startMinutes = timeToMinutes(startTime);
        let endMinutes = timeToMinutes(endTime);

        // Handle case where trip crosses midnight
        if (endMinutes < startMinutes) {
            endMinutes += 1440; // Add 24 hours in minutes
        }

        const duration = endMinutes - startMinutes;

        acc[vehicleNumber].trips.push({
            startTime: startTime,
            endTime: endTime,
            startMinutes: startMinutes,
            endMinutes: endMinutes,
            duration: Math.max(duration, 1), // Ensure minimum 1 minute duration
            status: trip.status,
            routeName: trip.routeName || "Unknown Route",
            tripType: trip.tripType || "Unknown"
        });

        return acc;
    }, {});

    // Convert to array format for the chart - each vehicle gets one row
    const chartData = [];

    Object.entries(groupedByVehicle).forEach(([vehicleNumber, vehicleData]) => {
        // Create one entry per vehicle with all its trips
        chartData.push({
            vehicleName: vehicleData.vehicleName,
            vehicleNumber: vehicleNumber,
            trips: vehicleData.trips.map((trip, index) => ({
                ...trip,
                tripIndex: index,
                tripId: `Trip-${index + 1}`,
                // Updated color logic for On Time and Delayed
                color: trip.status === "Completed" ? "#28a745" :  // Green for On Time
                    trip.status.includes("Not Completed") || trip.status.includes("over") ? "#ffc107" : "#6c757d"  // Red for Delayed
            }))
        });
    });

    return chartData;
};

// Helper function to convert time to minutes from 00:00
const timeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return (hours * 60) + (minutes || 0);
};
